import isNil from 'lodash/isNil';

export function getUnit(displayValue: string) {
  if (typeof displayValue !== 'string') return '';

  // 1. Match an optional - (minus) sign
  // 2. Match 1 or more numbers that contains a , (comma) or a . (dot)
  // 3. When you find the first thing that is not a number, start the capturing
  // group, this is going to be the unit.
  // Valid: 10.00% -> Unit: %
  // Valid: 123456.123456789km/s2 -> Unit: km/s2
  // Valid: -9.82m/s2 -> Unit: m/s2
  // Invalid: tree
  const unitMatcher = /-?[\d.,]+(.*)/;
  const result = displayValue.match(unitMatcher);
  if (result === null) return '';

  return result[1].trim();
}

export function getPrefix(displayValue: string) {
  if (typeof displayValue !== 'string') return '';

  const valueMatcher = /-?[\d.,]+(.*)/;
  const prefix = displayValue.replace(valueMatcher, '');

  return prefix;
}

export function getDecimals(value: number | string): number {
  let numericValue = value;
  if (typeof value === 'string') {
    const unit = getUnit(value);
    numericValue = value.replace(unit, '').trim();
  }

  return numericValue.toString().split('.')[1]?.length || 0;
}

export function formatNumber(value: number, opts?: Intl.NumberFormatOptions) {
  const userLocale = navigator.language || 'en';
  return new Intl.NumberFormat(userLocale, opts).format(value);
}

export function formatDisplayValue(displayValue: string) {
  if (displayValue.trim() === '') return displayValue;

  const prefix = getPrefix(displayValue);
  const unit = getUnit(displayValue);
  const numericValue = parseFloat(
    displayValue.replace(prefix, '').replace(unit, '').trim()
  );
  const decimals = getDecimals(displayValue);

  return (
    prefix +
    formatNumber(numericValue, { minimumFractionDigits: decimals }) +
    unit
  );
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export const numberToOrdinal = (number: number) => {
  let suffix = 'th';

  if (number < 4 || number > 20) {
    switch (number % 10) {
      case 1:
        suffix = 'st';
        break;
      case 2:
        suffix = 'nd';
        break;
      case 3:
        suffix = 'rd';
        break;
      default:
        break;
    }
  }

  return `${number}${suffix}`;
};

export const numberAsPercetange = (value: number | string, fractionDigits = 2) => {
  if (isNil(value)) return '-';

  return `${Number(value).toFixed(fractionDigits)}%`;
};
